import { saveSnapshot, useProjectStore } from "./projectStore";
import { storeFor } from "../storage";

/**
 * Tarayıcı sekmeleri arası eşgüdüm (Cem 2026-09-29): farklı sekmelerde farklı projeler açılabilir; AYNI proje ikinci bir
 * sekmede açılınca sessizce çatallanmaz — sonradan açan sekme salt okunur olur ve uyarı gösterir. "Bu sekmede düzenle"
 * düzenlemeyi devralır: öbür sekme bekleyen kaydını yazar, salt okunura geçer; bu sekme projeyi depodan yeniden yükler.
 * Kalıcı kilit yok (sekme kapanınca kendiliğinden düşer): açılışta "kim düzenliyor?" diye sorulur, düzenleyen yanıtlar.
 * Taşıyıcı BroadcastChannel; yoksa localStorage `storage` olayı.
 */
type Msg = { t: "who" | "mine" | "take" | "ack" | "released"; id: string; from: string; at?: number };
const TAB = Math.random().toString(36).slice(2) + Date.now().toString(36);
const KEY = "tasduvar:tabmsg";

function transport(onMsg: (m: Msg) => void): { post(m: Msg): void; close(): void } {
  if (typeof BroadcastChannel !== "undefined") {
    const ch = new BroadcastChannel("tasduvar-tabs");
    ch.onmessage = (e: MessageEvent<Msg>) => onMsg(e.data);
    return { post: (m) => ch.postMessage(m), close: () => ch.close() };
  }
  const onStorage = (e: StorageEvent): void => { if (e.key === KEY && e.newValue) { try { onMsg(JSON.parse(e.newValue) as Msg); } catch { /* bozuk */ } } };
  window.addEventListener("storage", onStorage);
  return {
    post: (m) => { try { window.localStorage.setItem(KEY, JSON.stringify({ ...m, n: Math.random() })); } catch { /* yasak */ } },
    close: () => window.removeEventListener("storage", onStorage),
  };
}

let post: ((m: Msg) => void) | null = null;
/** Devralma bekleyenleri: öbür sekmeden ack (varım) / released (yazdım, bıraktım) */
const waiting = new Map<string, (kind: "ack" | "released") => void>();

/** Bu sekme `id` projesini düzenliyor mu? */
const editing = (id: string): boolean => { const s = useProjectStore.getState(); return s.project?.id === id && !s.readOnly && s.origin !== "shared"; };

/** App açılışında bir kez; durdurucu döner. */
export function startTabSync(): () => void {
  const tr = transport((m) => {
    if (m.from === TAB) return;
    const s = useProjectStore.getState();
    if (m.t === "who" && editing(m.id)) tr.post({ t: "mine", id: m.id, from: TAB, at: s.openedAt });
    // İkimiz de düzenlemedeyiz: önce açan kazanır (eşitlikte sekme kimliği)
    else if (m.t === "mine" && editing(m.id) && ((m.at ?? 0) < s.openedAt || ((m.at ?? 0) === s.openedAt && m.from < TAB))) useProjectStore.setState({ readOnly: true, tabLock: "other" });
    else if (m.t === "take" && editing(m.id)) {
      tr.post({ t: "ack", id: m.id, from: TAB });
      useProjectStore.setState({ readOnly: true, tabLock: "taken", modified: false });   // hemen salt okunur: bu arada değişiklik olmasın
      void saveSnapshot({ ...s, readOnly: false }).finally(() => tr.post({ t: "released", id: m.id, from: TAB }));
    } else if (m.t === "ack" || m.t === "released") waiting.get(m.id)?.(m.t);
  });
  post = tr.post;
  // Her açılışta (openProject / newProject → openedAt değişir) sor
  const unsub = useProjectStore.subscribe((s, prev) => {
    if (s.openedAt !== prev.openedAt && s.project && !s.readOnly && s.origin !== "shared") tr.post({ t: "who", id: s.project.id, from: TAB, at: s.openedAt });
  });
  return () => { unsub(); tr.close(); post = null; };
}

/** "Bu sekmede düzenle": öbür sekmeden devral, projeyi depodaki son hâliyle yeniden aç (düzenlenebilir). */
export async function takeOver(ackWaitMs = 300, releaseWaitMs = 10_000): Promise<void> {
  const s = useProjectStore.getState();
  if (!s.project || !s.tabLock) return;
  const { id } = s.project, origin = s.origin;
  await new Promise<void>((resolve) => {
    let timer = setTimeout(done, ackWaitMs);   // kimse yanıt vermezse (sekme kapanmış) hemen devral
    function done(): void { clearTimeout(timer); waiting.delete(id); resolve(); }
    waiting.set(id, (kind) => { if (kind === "released") done(); else { clearTimeout(timer); timer = setTimeout(done, releaseWaitMs); } });
    post?.({ t: "take", id, from: TAB });
  });
  const fresh = await storeFor(origin).load(id).catch(() => null);
  const now = useProjectStore.getState();
  if (now.project?.id !== id) return;   // bu arada kapatıldı
  now.openProject(fresh ?? now.project, origin);
}
